import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareArrowUpRight } from "@fortawesome/pro-regular-svg-icons/faSquareArrowUpRight";
import { faFilter } from "@fortawesome/pro-regular-svg-icons/faFilter";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons/faInfoCircle";
import Close from "@mui/icons-material/Close";

import Tab from "@vapor/react-extended/Tab";
import Drawer from "@vapor/react-material/Drawer";
import IconButton from "@vapor/react-material/IconButton";
import TabContext from "@vapor/react-lab/TabContext";
import TabPanel from "@vapor/react-lab/TabPanel";

import { Tabs } from "./Tabs";
import Title, { TitleProps } from "../Title";

interface Content {
  tabKey: string;
  disabled?: boolean;
  icon?: React.ReactElement;
  content?: React.ReactElement;
}

export type StickyDrawerProps = {
  /**
   * Drawer title of the Title bar
   */
  title?: TitleProps["title"];
  /**
   * Drawer description of the Title bar
   */
  description?: TitleProps["description"];
  /**
   * Items positioned on the left of Title
   */
  leftTitleItems?: TitleProps["leftItems"];
  /**
   * Items positioned on the right of Title
   */
  rightItems?: TitleProps["rightItems"];
  /**
   * Details  for the first sticky tab. It has values like `value` (handles the value logic of tabs), `disabled` (disables the tab), icon (in case you want to change the default icon), `content` (the page content).
   */
  firstTabContent?: Content;
  /**
   * Details  for the second sticky tab. It has values like `value` (handles the value logic of tabs), `disabled` (disables the tab), icon (in case you want to change the default icon), `content` (the page content).
   */
  secondTabContent?: Content;
  /**
   * Details  for the third sticky tab. It has values like `value` (handles the value logic of tabs), `disabled` (disables the tab), icon (in case you want to change the default icon), `content` (the page content).
   */
  thirdTabContent?: Content;
  /**
   * Bottom sticky bar. You can use our Toolbar component.
   */
  bottomBar?: React.ReactElement;

  tabStore?: [
    tabValue: string,
    setTabValue: React.Dispatch<React.SetStateAction<string>>
  ];
};

export const StickyDrawer = (props: StickyDrawerProps) => {
  const [tabValue, setTabValue] = props.tabStore || React.useState("");

  const closeDawer = () => {
    setTabValue("");
  };

  const openDrawer = (newValue) => {
    setTabValue(newValue);
  };

  const rightItems =
    (props.rightItems && Array.isArray(props.rightItems)
      ? props.rightItems
      : [props.rightItems]) || [];

  return (
    <Drawer
      anchor="right"
      open={!!tabValue}
      onClose={closeDawer}
      hideBackdrop
      width={640}
      transitionDuration={150}
      ModalProps={{
        keepMounted: true
      }}
      sx={{
        "&.MuiDrawer-root .MuiPaper-root.MuiDrawer-paper": {
          overflow: "visible",
          visibility: "visible !important"
        }
      }}
    >
      <Tabs
        sticky
        orientation="vertical"
        value={tabValue || false}
        sx={{
          position: "absolute",
          left: "-48px",
          top: "50%",
          transform: "translate(0, -50%)"
        }}
      >
        {props.firstTabContent && (
          <Tab
            value={props.firstTabContent["tabKey"]}
            onClick={() => openDrawer(props.firstTabContent["tabKey"])}
            icon={
              props.firstTabContent.icon ? (
                props.firstTabContent.icon
              ) : (
                <FontAwesomeIcon icon={faSquareArrowUpRight} fontSize="14px" />
              )
            }
          />
        )}
        {props.secondTabContent && (
          <Tab
            value={props.secondTabContent["tabKey"]}
            onClick={() => openDrawer(props.secondTabContent["tabKey"])}
            icon={
              props.secondTabContent.icon ? (
                props.secondTabContent.icon
              ) : (
                <FontAwesomeIcon icon={faFilter} fontSize="14px" />
              )
            }
          />
        )}
        {props.thirdTabContent && (
          <Tab
            value={props.thirdTabContent["tabKey"]}
            onClick={() => openDrawer(props.thirdTabContent["tabKey"])}
            icon={
              props.thirdTabContent.icon ? (
                props.thirdTabContent.icon
              ) : (
                <FontAwesomeIcon icon={faInfoCircle} fontSize="14px" />
              )
            }
            disabled={props.thirdTabContent["disabled"]}
          />
        )}
      </Tabs>
      <div style={{ height: "100%", overflowY: "auto" }}>
        <Title
          leftItems={props.leftTitleItems}
          title={props.title}
          description={props.description}
          divider
          rightItems={[
            ...rightItems,
            <IconButton color="primary" onClick={closeDawer}>
              <Close />
            </IconButton>
          ]}
        />
        <TabContext value={tabValue}>
          {props.firstTabContent && (
            <TabPanel value={props.firstTabContent["tabKey"]}>
              {props.firstTabContent["content"]}
            </TabPanel>
          )}
          {props.secondTabContent && (
            <TabPanel value={props.secondTabContent["tabKey"]}>
              {props.secondTabContent["content"]}
            </TabPanel>
          )}
          {props.thirdTabContent && (
            <TabPanel value={props.thirdTabContent["tabKey"]}>
              {props.thirdTabContent["content"]}
            </TabPanel>
          )}
        </TabContext>

        {props.bottomBar || null}
      </div>
    </Drawer>
  );
};
